// ENHANCED HEALTH CONNECT SERVICE FÜR SAMSUNG GALAXY WATCH
// Echte Google Health Connect API Integration mit Supabase Sync

import { masterSupabase as supabase } from "../lib/supabase-master.js";

class HealthConnectService {
  constructor() {
    this.isConnected = false;
    this.isInitialized = false;
    this.lastSyncTime = null;
    this.syncInterval = null;
    this.healthData = {
      steps: 0,
      heartRate: 0,
      calories: 0,
      sleep: 0,
      activeMinutes: 0,
      distance: 0,
      workouts: [],
      lastUpdated: null
    };
    
    // Health Connect API Konfiguration
    this.permissions = [
      'android.permission.health.READ_STEPS',
      'android.permission.health.READ_HEART_RATE', 
      'android.permission.health.READ_SLEEP',
      'android.permission.health.READ_EXERCISE',
      'android.permission.health.READ_TOTAL_CALORIES_BURNED',
      'android.permission.health.READ_DISTANCE',
      'android.permission.health.READ_ACTIVE_CALORIES_BURNED'
    ];
    
    this.localStorageKey = 'clara_health_connect_data';
    this.loadLocalData();
  }

  // HAUPTINITIALISIERUNG
  async initialize() {
    try {
      console.log('🚀 Health Connect Service wird initialisiert...');
      
      // 1. Prüfe Health Connect Verfügbarkeit
      const isAvailable = await this.checkHealthConnectAvailability();
      if (!isAvailable) {
        console.log('⚠️ Health Connect nicht verfügbar - verwende Mock-Daten');
        this.initializeMockMode();
        return true;
      }

      // 2. Fordere Berechtigungen an
      const permissionsGranted = await this.requestPermissions();
      if (!permissionsGranted) {
        console.log('⚠️ Berechtigungen verweigert - verwende Mock-Daten');
        this.initializeMockMode();
        return true;
      }

      // 3. Erste Datensynchronisation
      await this.performInitialSync();
      
      // 4. Starte automatische Synchronisation
      this.startAutoSync();
      
      this.isConnected = true;
      this.isInitialized = true;
      console.log('✅ Health Connect Service erfolgreich initialisiert');
      
      return true;
    } catch (error) {
      console.error('❌ Health Connect Initialisierung fehlgeschlagen:', error);
      this.initializeMockMode();
      return false;
    }
  }

  // HEALTH CONNECT VERFÜGBARKEIT PRÜFEN
  async checkHealthConnectAvailability() {
    try {
      // Prüfe ob wir in einem Android WebView mit Health Connect sind
      if (typeof window !== 'undefined' && window.Android && window.Android.healthConnect) {
        console.log('📱 Android Health Connect Interface gefunden');
        return true;
      }
      
      // Prüfe Web Health Connect API (falls verfügbar)
      if (typeof window !== 'undefined' && window.HealthConnect) {
        console.log('🌐 Web Health Connect API gefunden');
        return true;
      }

      // Prüfe Samsung Health Web API
      if (typeof window !== 'undefined' && window.SamsungHealth) {
        console.log('⌚ Samsung Health Web API gefunden');
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ Health Connect Verfügbarkeitsprüfung fehlgeschlagen:', error);
      return false;
    }
  }

  // BERECHTIGUNGEN ANFORDERN
  async requestPermissions() {
    try {
      console.log('🔐 Fordere Health Connect Berechtigungen an...');
      
      // Android Health Connect
      if (window.Android && window.Android.healthConnect) {
        const result = await window.Android.healthConnect.requestPermissions(
          JSON.stringify(this.permissions)
        );
        const granted = JSON.parse(result);
        console.log('✅ Android Berechtigungen erhalten:', granted);
        return granted.every(permission => permission.granted);
      }
      
      // Web Health Connect API
      if (window.HealthConnect) {
        const granted = await window.HealthConnect.requestPermissions(this.permissions);
        console.log('✅ Web Berechtigungen erhalten:', granted);
        return granted;
      }

      // Samsung Health Web API
      if (window.SamsungHealth) {
        const granted = await window.SamsungHealth.requestPermissions(this.permissions);
        console.log('✅ Samsung Health Berechtigungen erhalten:', granted);
        return granted;
      }

      return false;
    } catch (error) {
      console.error('❌ Berechtigungsanfrage fehlgeschlagen:', error);
      return false;
    }
  }

  // ERSTE DATENSYNCHRONISATION
  async performInitialSync() {
    try {
      console.log('🔄 Führe erste Datensynchronisation durch...');
      
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 7); // Letzte 7 Tage
      
      const healthData = await this.fetchAllHealthData(startDate, endDate);
      
      // Speichere in lokaler Struktur
      this.healthData = {
        ...healthData,
        lastUpdated: new Date().toISOString()
      };
      
      // Speichere lokal
      this.saveLocalData();
      
      // Synchronisiere mit Supabase
      await this.syncToSupabase();
      
      this.lastSyncTime = new Date().toISOString();
      console.log('✅ Erste Synchronisation abgeschlossen');
      
    } catch (error) {
      console.error('❌ Erste Synchronisation fehlgeschlagen:', error);
    }
  }

  // ALLE GESUNDHEITSDATEN ABRUFEN
  async fetchAllHealthData(startDate, endDate) {
    try {
      console.log(`📊 Rufe Gesundheitsdaten ab (${startDate.toDateString()} - ${endDate.toDateString()})`);
      
      const [steps, heartRate, sleep, calories, activeMinutes, distance, workouts] = await Promise.all([
        this.fetchStepsData(startDate, endDate),
        this.fetchHeartRateData(startDate, endDate),
        this.fetchSleepData(startDate, endDate),
        this.fetchCaloriesData(startDate, endDate),
        this.fetchActiveMinutesData(startDate, endDate),
        this.fetchDistanceData(startDate, endDate),
        this.fetchWorkoutData(startDate, endDate)
      ]);

      return {
        steps: steps || 0,
        heartRate: heartRate || 0,
        calories: calories || 0,
        sleep: sleep || 0,
        activeMinutes: activeMinutes || 0,
        distance: distance || 0,
        workouts: workouts || []
      };
      
    } catch (error) {
      console.error('❌ Gesundheitsdaten abrufen fehlgeschlagen:', error);
      return this.getLocalData();
    }
  }

  // SCHRITTE ABRUFEN
  async fetchStepsData(startDate, endDate) {
    try {
      let stepsData = [];
      
      // Android Health Connect
      if (window.Android && window.Android.healthConnect) {
        const result = await window.Android.healthConnect.readSteps(
          startDate.toISOString(),
          endDate.toISOString()
        );
        stepsData = JSON.parse(result);
      }
      // Web Health Connect
      else if (window.HealthConnect) {
        stepsData = await window.HealthConnect.readRecords('StepsRecord', {
          timeRangeFilter: {
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString()
          }
        });
      }
      // Samsung Health Web API
      else if (window.SamsungHealth) {
        stepsData = await window.SamsungHealth.readSteps(startDate, endDate);
      }

      const totalSteps = stepsData.reduce((sum, record) => {
        return sum + (record.count || record.steps || 0);
      }, 0);
      
      console.log(`👣 Schritte abgerufen: ${totalSteps.toLocaleString()}`);
      return totalSteps;
      
    } catch (error) {
      console.error('❌ Schritte abrufen fehlgeschlagen:', error);
      return 0;
    }
  }

  // HERZFREQUENZ ABRUFEN
  async fetchHeartRateData(startDate, endDate) {
    try {
      let heartRateData = [];
      
      // Android Health Connect
      if (window.Android && window.Android.healthConnect) {
        const result = await window.Android.healthConnect.readHeartRate(
          startDate.toISOString(),
          endDate.toISOString()
        );
        heartRateData = JSON.parse(result);
      }
      // Web Health Connect
      else if (window.HealthConnect) {
        heartRateData = await window.HealthConnect.readRecords('HeartRateRecord', {
          timeRangeFilter: {
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString()
          }
        });
      }
      // Samsung Health Web API
      else if (window.SamsungHealth) {
        heartRateData = await window.SamsungHealth.readHeartRate(startDate, endDate);
      }

      if (heartRateData.length === 0) return 0;

      // Berechne Durchschnitt der letzten Messungen
      const recentData = heartRateData.slice(-10); // Letzte 10 Messungen
      const avgHeartRate = recentData.reduce((sum, record) => {
        return sum + (record.beatsPerMinute || record.heartRate || 0);
      }, 0) / recentData.length;
      
      console.log(`❤️ Herzfrequenz abgerufen: ${Math.round(avgHeartRate)} bpm`);
      return Math.round(avgHeartRate);
      
    } catch (error) {
      console.error('❌ Herzfrequenz abrufen fehlgeschlagen:', error);
      return 0;
    }
  }

  // SCHLAF ABRUFEN
  async fetchSleepData(startDate, endDate) {
    try {
      let sleepData = [];
      
      // Android Health Connect
      if (window.Android && window.Android.healthConnect) {
        const result = await window.Android.healthConnect.readSleep(
          startDate.toISOString(),
          endDate.toISOString()
        );
        sleepData = JSON.parse(result);
      }
      // Web Health Connect
      else if (window.HealthConnect) {
        sleepData = await window.HealthConnect.readRecords('SleepSessionRecord', {
          timeRangeFilter: {
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString()
          }
        });
      }
      // Samsung Health Web API
      else if (window.SamsungHealth) {
        sleepData = await window.SamsungHealth.readSleep(startDate, endDate);
      }

      // Berechne durchschnittliche Schlafdauer
      const totalSleepHours = sleepData.reduce((sum, record) => {
        let duration = 0;
        if (record.duration) {
          duration = record.duration / (1000 * 60 * 60); // Millisekunden zu Stunden
        } else if (record.endTime && record.startTime) {
          duration = (new Date(record.endTime) - new Date(record.startTime)) / (1000 * 60 * 60);
        }
        return sum + duration;
      }, 0);

      const avgSleepHours = sleepData.length > 0 ? totalSleepHours / sleepData.length : 0;
      
      console.log(`😴 Schlaf abgerufen: ${avgSleepHours.toFixed(1)} Stunden`);
      return parseFloat(avgSleepHours.toFixed(1));
      
    } catch (error) {
      console.error('❌ Schlaf abrufen fehlgeschlagen:', error);
      return 0;
    }
  }

  // KALORIEN ABRUFEN
  async fetchCaloriesData(startDate, endDate) {
    try {
      let caloriesData = [];
      
      // Android Health Connect
      if (window.Android && window.Android.healthConnect) {
        const result = await window.Android.healthConnect.readCalories(
          startDate.toISOString(),
          endDate.toISOString()
        );
        caloriesData = JSON.parse(result);
      }
      // Web Health Connect
      else if (window.HealthConnect) {
        caloriesData = await window.HealthConnect.readRecords('TotalCaloriesBurnedRecord', {
          timeRangeFilter: {
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString()
          }
        });
      }
      // Samsung Health Web API
      else if (window.SamsungHealth) {
        caloriesData = await window.SamsungHealth.readCalories(startDate, endDate);
      }

      const totalCalories = caloriesData.reduce((sum, record) => {
        return sum + (record.energy?.inCalories || record.calories || 0);
      }, 0);
      
      console.log(`🔥 Kalorien abgerufen: ${totalCalories.toLocaleString()}`);
      return Math.round(totalCalories);
      
    } catch (error) {
      console.error('❌ Kalorien abrufen fehlgeschlagen:', error);
      return 0;
    }
  }

  // AKTIVE MINUTEN ABRUFEN
  async fetchActiveMinutesData(startDate, endDate) {
    try {
      let activeData = [];
      
      // Android Health Connect
      if (window.Android && window.Android.healthConnect) {
        const result = await window.Android.healthConnect.readActiveMinutes(
          startDate.toISOString(),
          endDate.toISOString()
        );
        activeData = JSON.parse(result);
      }
      // Web Health Connect
      else if (window.HealthConnect) {
        activeData = await window.HealthConnect.readRecords('ExerciseSessionRecord', {
          timeRangeFilter: {
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString()
          }
        });
      }
      // Samsung Health Web API
      else if (window.SamsungHealth) {
        activeData = await window.SamsungHealth.readActiveMinutes(startDate, endDate);
      }

      const totalActiveMinutes = activeData.reduce((sum, record) => {
        let minutes = 0;
        if (record.duration) {
          minutes = record.duration / (1000 * 60); // Millisekunden zu Minuten
        } else if (record.endTime && record.startTime) {
          minutes = (new Date(record.endTime) - new Date(record.startTime)) / (1000 * 60);
        }
        return sum + minutes;
      }, 0);
      
      console.log(`⚡ Aktive Minuten abgerufen: ${Math.round(totalActiveMinutes)}`);
      return Math.round(totalActiveMinutes);
      
    } catch (error) {
      console.error('❌ Aktive Minuten abrufen fehlgeschlagen:', error);
      return 0;
    }
  }

  // DISTANZ ABRUFEN
  async fetchDistanceData(startDate, endDate) {
    try {
      let distanceData = [];
      
      // Android Health Connect
      if (window.Android && window.Android.healthConnect) {
        const result = await window.Android.healthConnect.readDistance(
          startDate.toISOString(),
          endDate.toISOString()
        );
        distanceData = JSON.parse(result);
      }
      // Web Health Connect
      else if (window.HealthConnect) {
        distanceData = await window.HealthConnect.readRecords('DistanceRecord', {
          timeRangeFilter: {
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString()
          }
        });
      }
      // Samsung Health Web API
      else if (window.SamsungHealth) {
        distanceData = await window.SamsungHealth.readDistance(startDate, endDate);
      }

      const totalDistance = distanceData.reduce((sum, record) => {
        return sum + (record.distance?.inKilometers || record.distance || 0);
      }, 0);
      
      console.log(`🏃 Distanz abgerufen: ${totalDistance.toFixed(1)} km`);
      return parseFloat(totalDistance.toFixed(1));
      
    } catch (error) {
      console.error('❌ Distanz abrufen fehlgeschlagen:', error);
      return 0;
    }
  }

  // WORKOUTS ABRUFEN
  async fetchWorkoutData(startDate, endDate) {
    try {
      let workoutData = [];
      
      // Android Health Connect
      if (window.Android && window.Android.healthConnect) {
        const result = await window.Android.healthConnect.readWorkouts(
          startDate.toISOString(),
          endDate.toISOString()
        );
        workoutData = JSON.parse(result);
      }
      // Web Health Connect
      else if (window.HealthConnect) {
        workoutData = await window.HealthConnect.readRecords('ExerciseSessionRecord', {
          timeRangeFilter: {
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString()
          }
        });
      }
      // Samsung Health Web API
      else if (window.SamsungHealth) {
        workoutData = await window.SamsungHealth.readWorkouts(startDate, endDate);
      }

      const workouts = workoutData.map(record => ({
        id: record.metadata?.id || record.id || Date.now() + Math.random(),
        title: record.exerciseType || record.title || 'Workout',
        duration: Math.round((new Date(record.endTime) - new Date(record.startTime)) / (1000 * 60)),
        calories: record.totalEnergyBurned?.inCalories || record.calories || 0,
        startTime: record.startTime,
        endTime: record.endTime,
        type: record.exerciseType || 'unknown'
      }));

      console.log(`🏋️ Workouts abgerufen: ${workouts.length}`);
      return workouts;
      
    } catch (error) {
      console.error('❌ Workouts abrufen fehlgeschlagen:', error);
      return [];
    }
  }

  // AUTOMATISCHE SYNCHRONISATION
  startAutoSync(intervalMinutes = 5) {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = setInterval(async () => {
      try {
        console.log('🔄 Automatische Synchronisation...');
        await this.performIncrementalSync();
      } catch (error) {
        console.error('❌ Automatische Synchronisation fehlgeschlagen:', error);
      }
    }, intervalMinutes * 60 * 1000);

    console.log(`⏰ Automatische Synchronisation gestartet (alle ${intervalMinutes} Minuten)`);
  }

  // INKREMENTELLE SYNCHRONISATION
  async performIncrementalSync() {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setHours(startDate.getHours() - 1); // Letzte Stunde
      
      const freshData = await this.fetchAllHealthData(startDate, endDate);
      
      // Update nur wenn sich Daten geändert haben
      const hasChanges = JSON.stringify(freshData) !== JSON.stringify(this.healthData);
      
      if (hasChanges) {
        this.healthData = {
          ...freshData,
          lastUpdated: new Date().toISOString()
        };
        
        this.saveLocalData();
        await this.syncToSupabase();
        this.lastSyncTime = new Date().toISOString();
        
        console.log('✅ Inkrementelle Synchronisation abgeschlossen');
      } else {
        console.log('ℹ️ Keine neuen Daten - Synchronisation übersprungen');
      }
      
    } catch (error) {
      console.error('❌ Inkrementelle Synchronisation fehlgeschlagen:', error);
    }
  }

  // SUPABASE SYNCHRONISATION
  async syncToSupabase() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('⚠️ Benutzer nicht angemeldet - überspringe Supabase Sync');
        return false;
      }

      const today = new Date().toISOString().split('T')[0];
      
      // 1. Tägliche Zusammenfassung aktualisieren
      const summaryData = {
        user_id: user.id,
        date: today,
        total_steps: this.healthData.steps,
        calories_burned: this.healthData.calories,
        active_minutes: this.healthData.activeMinutes,
        sleep_hours: this.healthData.sleep,
        resting_heart_rate: this.healthData.heartRate,
        average_heart_rate: this.healthData.heartRate,
        workouts_completed: this.healthData.workouts.length,
        sync_status: 'synced',
        last_synced_at: new Date().toISOString()
      };

      const { error: summaryError } = await supabase
        .from('daily_health_summary')
        .upsert(summaryData, { onConflict: 'user_id,date' });

      if (summaryError) {
        console.error('❌ Tägliche Zusammenfassung Sync fehlgeschlagen:', summaryError);
      } else {
        console.log('✅ Tägliche Zusammenfassung synchronisiert');
      }

      // 2. Rohdaten speichern
      const rawDataEntries = [
        { data_type: 'steps', value_numeric: this.healthData.steps, unit: 'steps' },
        { data_type: 'heart_rate', value_numeric: this.healthData.heartRate, unit: 'bpm' },
        { data_type: 'calories', value_numeric: this.healthData.calories, unit: 'calories' },
        { data_type: 'sleep', value_numeric: this.healthData.sleep, unit: 'hours' },
        { data_type: 'active_minutes', value_numeric: this.healthData.activeMinutes, unit: 'minutes' },
        { data_type: 'distance', value_numeric: this.healthData.distance, unit: 'km' }
      ].map(entry => ({
        user_id: user.id,
        ...entry,
        data_source: 'samsung_health',
        recorded_at: new Date().toISOString(),
        synced_at: new Date().toISOString()
      }));

      const { error: rawDataError } = await supabase
        .from('health_connect_data')
        .insert(rawDataEntries);

      if (rawDataError) {
        console.error('❌ Rohdaten Sync fehlgeschlagen:', rawDataError);
      } else {
        console.log('✅ Rohdaten synchronisiert');
      }

      // 3. Sync-Log erstellen
      const { error: logError } = await supabase
        .from('health_sync_log')
        .insert({
          user_id: user.id,
          sync_type: 'incremental',
          data_types: ['steps', 'heart_rate', 'calories', 'sleep', 'active_minutes', 'distance'],
          records_synced: rawDataEntries.length,
          sync_status: 'success',
          sync_completed_at: new Date().toISOString()
        });

      if (logError) {
        console.error('❌ Sync-Log fehlgeschlagen:', logError);
      }

      return true;
      
    } catch (error) {
      console.error('❌ Supabase Synchronisation fehlgeschlagen:', error);
      return false;
    }
  }

  // MOCK-MODUS FÜR ENTWICKLUNG
  initializeMockMode() {
    console.log('🎭 Mock-Modus aktiviert');
    this.isConnected = false;
    this.isInitialized = true;
    
    // Generiere realistische Mock-Daten
    this.healthData = {
      steps: 8500 + Math.floor(Math.random() * 3000),
      heartRate: 72 + Math.floor(Math.random() * 20),
      calories: 2200 + Math.floor(Math.random() * 500),
      sleep: 7.5 + (Math.random() * 2 - 1),
      activeMinutes: 30 + Math.floor(Math.random() * 60),
      distance: 5.5 + (Math.random() * 3),
      workouts: [
        {
          id: 1,
          title: 'Morning Run',
          duration: 30,
          calories: 300,
          startTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          type: 'running'
        },
        {
          id: 2,
          title: 'Strength Training',
          duration: 45,
          calories: 250,
          startTime: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          type: 'strength'
        }
      ],
      lastUpdated: new Date().toISOString()
    };
    
    this.saveLocalData();
    
    // Starte Mock-Updates für Entwicklung
    this.startAutoSync(5); // Alle 5 Minuten auch im Mock-Modus
  }

  // LOKALE SPEICHERUNG
  saveLocalData() {
    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify({
        healthData: this.healthData,
        lastSyncTime: this.lastSyncTime,
        isConnected: this.isConnected
      }));
      console.log('💾 Health Data lokal gespeichert');
    } catch (error) {
      console.error('❌ Lokale Speicherung fehlgeschlagen:', error);
    }
  }

  loadLocalData() {
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      if (stored) {
        const data = JSON.parse(stored);
        this.healthData = data.healthData || this.healthData;
        this.lastSyncTime = data.lastSyncTime;
        this.isConnected = data.isConnected || false;
        console.log('📂 Health Data aus lokalem Speicher geladen');
      }
    } catch (error) {
      console.error('❌ Lokale Daten laden fehlgeschlagen:', error);
    }
  }

  getLocalData() {
    return this.healthData;
  }

  // HEALTH SCORE BERECHNUNG
  calculateHealthScore() {
    const { steps, heartRate, sleep, activeMinutes } = this.healthData;
    
    let score = 0;
    
    // Schritte (max 25 Punkte)
    if (steps >= 10000) score += 25;
    else if (steps >= 8000) score += 20;
    else if (steps >= 6000) score += 15;
    else if (steps >= 4000) score += 10;
    else score += Math.floor(steps / 400);
    
    // Herzfrequenz (max 25 Punkte)
    if (heartRate >= 60 && heartRate <= 100) score += 25;
    else if (heartRate >= 50 && heartRate <= 110) score += 20;
    else score += 10;
    
    // Schlaf (max 25 Punkte)
    if (sleep >= 7 && sleep <= 9) score += 25;
    else if (sleep >= 6 && sleep <= 10) score += 20;
    else score += 10;
    
    // Aktivität (max 25 Punkte)
    if (activeMinutes >= 30) score += 25;
    else if (activeMinutes >= 20) score += 20;
    else if (activeMinutes >= 10) score += 15;
    else score += Math.floor(activeMinutes / 2);
    
    return Math.min(score, 100);
  }

  // ÖFFENTLICHE API
  async getHealthData() {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    return {
      ...this.healthData,
      healthScore: this.calculateHealthScore(),
      isLive: this.isConnected,
      lastSync: this.lastSyncTime,
      isConnected: this.isConnected
    };
  }

  // CLEANUP
  destroy() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    console.log('🧹 Health Connect Service bereinigt');
  }
}

export default HealthConnectService;

